import React from 'react';
import { motion } from 'framer-motion';
import { BarChart3, ShieldCheck, Target, Zap, TrendingUp, Settings2 } from 'lucide-react';

const features = [
  {
    title: 'High Fill Rate',
    description: 'Maximize your campaign’s reach with consistently high fill rates in every region',
    icon: <TrendingUp className="h-10 w-10 text-neon-pink" />,
    color: 'neon-pink',
  },
  {
    title: 'Smart Traffic Matching',
    description: 'Our AI-powered algorithms connect your ads with the most relevant audience for optimal ROI',
    icon: <Zap className="h-10 w-10 text-neon-blue" />,
    color: 'neon-blue',
  },
  {
    title: 'Advanced Targeting',
    description: 'Pinpoint your ideal customers with granular targeting options including geo, device, and more',
    icon: <Target className="h-10 w-10 text-neon-purple" />,
    color: 'neon-purple',
  },
  {
    title: 'Only Pay for Clean Traffic',
    description: 'We screen every click with AI and human eyes, then bill you only for traffic we know is clean and brand-safe',
    icon: <ShieldCheck className="h-10 w-10 text-neon-pink" />,
    color: 'neon-pink',
  },
  {
    title: 'Intuitive Campaign Data Table',
    description: 'Quickly view all your campaign metrics in a clean, tabular format',
    icon: <BarChart3 className="h-10 w-10 text-neon-blue" />,
    color: 'neon-blue',
  },
  {
    title: 'Easy-to-Use Interface',
    description: 'Create and manage your ads effortlessly with our modern, visual platform',
    icon: <Settings2 className="h-10 w-10 text-neon-purple" />,
    color: 'neon-purple',
  }
];

const FeaturesSection = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.95 },
    visible: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: { duration: 0.5, ease: "easeOut" }
    },
    hover: {
      scale: 1.05,
      boxShadow: "0 0 25px var(--hover-color), 0 0 10px var(--hover-color) inset",
      transition: { duration: 0.3 }
    }
  };

  return (
    <section id="features" className="py-16 md:py-24">
      <div className="container mx-auto px-4">
        <motion.h2 
          className="text-3xl md:text-4xl font-bold text-center mb-4 text-glow-blue animated-text-glow"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.5 }}
        >
          Why Choose Galaxioid?
        </motion.h2>
        <motion.p 
          className="text-lg text-cyber-text-secondary text-center mb-12 md:mb-16 max-w-2xl mx-auto animated-text-glow"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          Fuel your campaigns with speed, rock-solid security, and measurable results.
        </motion.p>
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              className={`p-6 rounded-lg glassmorphism border border-${feature.color}/50`}
              variants={itemVariants}
              custom={{ '--hover-color': `var(--${feature.color})` }}
              whileHover="hover"
            >
              <div className={`p-4 inline-block rounded-lg bg-${feature.color}/10 mb-4 border border-${feature.color}/30 shadow-sm shadow-${feature.color}`}>
                {React.cloneElement(feature.icon, { className: `h-10 w-10 text-${feature.color}` })}
              </div>
              <h3 className={`text-xl font-semibold mb-2 text-glow-${feature.color} animated-text-glow`}>
                {feature.title}
              </h3>
              <p className="text-cyber-text-secondary text-sm animated-text-glow">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default FeaturesSection;
